/**
 * Recovery Trauma™ — issue reporting and the Admin → Issues area.
 *
 * `reportIssue` is deliberately callable without a session so a member can
 * report a problem even when sign-in itself is what's broken. It is heavily
 * clamped: fixed field lengths, a noise filter, dedupe by fingerprint and an
 * hourly re-alert throttle.
 */

import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { assertAdmin } from "./admin.server";
import type { Json } from "@/integrations/supabase/types";

export type IssueStatus = "new" | "investigating" | "resolved";

export interface AdminIssue {
  id: string;
  source: string;
  severity: string;
  title: string;
  detail: string | null;
  feature: string | null;
  page_path: string | null;
  user_id: string | null;
  user_email: string | null;
  user_agent: string | null;
  platform: string | null;
  app_version: string | null;
  context: Json | null;
  status: IssueStatus;
  occurrences: number;
  first_seen: string;
  last_seen: string;
  notified_at: string | null;
  admin_notes: string | null;
  resolved_at: string | null;
}

const ALLOWED_SOURCES = new Set([
  "user_report",
  "client_error",
  "server_error",
  "payment",
  "email",
  "auth",
  "audio",
  "ai",
  "upload",
  "moderation",
  "other",
]);

export interface ReportIssueInput {
  source?: string;
  severity?: "critical" | "error" | "warning";
  title: string;
  detail?: string;
  feature?: string;
  pagePath?: string;
  userAgent?: string;
  platform?: string;
  appVersion?: string;
  context?: Record<string, unknown>;
}

/**
 * Called from the "Report a problem" form and from the global error capture.
 * Returns `{ ok: true }` even when the report is filtered as noise so the
 * member never sees a failure for reporting a failure.
 */
export const reportIssue = createServerFn({ method: "POST" })
  .inputValidator((input: ReportIssueInput) => {
    const title = String(input?.title ?? "").trim().slice(0, 300);
    if (!title) throw new Error("A short description is required.");
    const source = ALLOWED_SOURCES.has(String(input?.source)) ? String(input.source) : "user_report";
    return {
      source,
      severity: input?.severity === "critical" || input?.severity === "warning" ? input.severity : "error",
      title,
      detail: input?.detail ? String(input.detail).slice(0, 8000) : undefined,
      feature: input?.feature ? String(input.feature).slice(0, 120) : undefined,
      pagePath: input?.pagePath ? String(input.pagePath).slice(0, 300) : undefined,
      userAgent: input?.userAgent ? String(input.userAgent).slice(0, 500) : undefined,
      platform: input?.platform ? String(input.platform).slice(0, 120) : undefined,
      appVersion: input?.appVersion ? String(input.appVersion).slice(0, 60) : undefined,
      context: input?.context && typeof input.context === "object" ? input.context : undefined,
    } as Required<Pick<ReportIssueInput, "title">> & ReportIssueInput;
  })
  .handler(async ({ data }) => {
    const { recordIssueSafe } = await import("./issues.server");

    // Identify the member when a session is present; never require one.
    let userId: string | null = null;
    let userEmail: string | null = null;
    try {
      const auth = getRequestHeader("authorization");
      const token = auth?.replace(/^Bearer\s+/i, "");
      if (token) {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: userRes } = await supabaseAdmin.auth.getUser(token);
        userId = userRes?.user?.id ?? null;
        userEmail = userRes?.user?.email ?? null;
      }
    } catch {
      /* anonymous report — fine */
    }

    await recordIssueSafe({
      source: data.source as never,
      severity: data.severity as never,
      title: data.title,
      detail: data.detail ?? null,
      feature: data.feature ?? null,
      pagePath: data.pagePath ?? null,
      userId,
      userEmail,
      userAgent: data.userAgent ?? null,
      platform: data.platform ?? null,
      appVersion: data.appVersion ?? null,
      context: data.context,
    });

    return { ok: true as const };
  });

/* ------------------------------------------------------------------ */
/* Admin → Issues                                                      */
/* ------------------------------------------------------------------ */

export const listIssues = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input?: { status?: IssueStatus | "all"; source?: string }) => ({
    status: input?.status ?? "all",
    source: input?.source ?? "all",
  }))
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await assertAdmin(context.userId);
    let q = supabaseAdmin
      .from("admin_issues")
      .select("*")
      .order("last_seen", { ascending: false })
      .limit(400);
    if (data.status !== "all") q = q.eq("status", data.status);
    if (data.source !== "all") q = q.eq("source", data.source);
    const { data: rows, error } = await q;
    if (error) throw new Error(error.message);

    const issues = (rows ?? []) as unknown as AdminIssue[];
    const counts = { new: 0, investigating: 0, resolved: 0 } as Record<IssueStatus, number>;
    const { data: allStatuses } = await supabaseAdmin.from("admin_issues").select("status");
    for (const r of (allStatuses ?? []) as { status: IssueStatus }[]) {
      if (counts[r.status] !== undefined) counts[r.status] += 1;
    }
    return { issues, counts };
  });

export const updateIssue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string; status?: IssueStatus; admin_notes?: string }) => {
    if (!input?.id) throw new Error("Missing issue id.");
    return input;
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await assertAdmin(context.userId);
    const patch: { status?: IssueStatus; resolved_at?: string | null; admin_notes?: string } = {};
    if (data.status) {
      patch.status = data.status;
      patch.resolved_at = data.status === "resolved" ? new Date().toISOString() : null;
    }
    if (data.admin_notes !== undefined) patch.admin_notes = data.admin_notes;
    const { error } = await supabaseAdmin.from("admin_issues").update(patch).eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

export const deleteIssue = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((input: { id: string }) => {
    if (!input?.id) throw new Error("Missing issue id.");
    return input;
  })
  .handler(async ({ data, context }) => {
    const supabaseAdmin = await assertAdmin(context.userId);
    const { error } = await supabaseAdmin.from("admin_issues").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });
